/**
 * @date: 2022-04-12
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */

import React, {useEffect, useState} from "react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    styled,
    TableCell,
    TableContainer,
    Tooltip,
    Typography
} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import {convertDateToTimeString} from "../utils/cert-helper";
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import {useCookies} from "react-cookie";
import {sortedRowInformation} from "../Hisps/HispsAsList";

const StyledTableRow = styled(TableRow)(({theme}) => ({
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));
const StyledTableRowHeader = styled(TableRow)({
    '& .MuiTableCell-head': {
        fontWeight: 'bold'
    },
})
const StyledInfoDiv = styled('div')({
    display: 'flex',
    gap: '5px',
    alignItems: 'center'
})

/** Overall score used for sorting: (error + warning + alert) / inputRecordCount */
export const calculateScore = (alert, error, warning, totalCount) => {
    return ((alert + error + warning) / totalCount).toFixed(2);
}

/** Per-category percentage: count / inputRecordCount × 100, formatted as "X.X%" */
const toPct = (count, total) => {
    if (!total || total === 0) return '0.0%';
    return `${((count / total) * 100).toFixed(1)}%`;
}

export default function PublishedScoreTable(props) {
    const contributions = props.contributions || [];
    const hispsLogo = props.hispsLogo;

    const [sortedContributions, setSortedContributions] = useState([]);
    const [options, setOptions] = useState(0);

    const [cookie, setCookie] = useCookies(['scoreOption']);

    useEffect(() => {
        const sorted = contributions.length > 0 && sortedRowInformation(contributions, getComparator('asc'))
        if (sorted.length === 0) {
            console.error(`Contribution not found`)
            return;
        }
        if (cookie.scoreOption) {
            if (cookie.scoreOption > 0) {
                setOptions(cookie.scoreOption);
                setSortedContributions(sorted.slice(0, cookie.scoreOption))
            } else {
                setOptions(0);
                setSortedContributions(sorted.slice());
            }
        } else {
            setSortedContributions(sorted.slice());
        }
    }, [options])

    const getComparator = (order) => {
        return order === 'desc'
            ? (a, b) => comparator(a, b)
            : (a, b) => -comparator(a, b)
    }

    const comparator = (a, b) => {
        const o1 = calculateScore(a.alertCount, a.errorCount, a.warningCount, a.inputRecordCount);
        const o2 = calculateScore(b.alertCount, b.errorCount, b.warningCount, b.inputRecordCount);
        if (o2 < o1) return -1;
        else if (o2 > o1) return 1;
        else return 0;
    }

    const handleCookie = (o) => {
        let expires = new Date();
        expires.setMonth(expires.getMonth() + 1);
        setCookie('scoreOption', o, { path: '/', expires });
    }

    const handleSelectChange = (event) => {
        const { value } = event.target;
        setOptions(value);
        handleCookie(value);
    }

    const downloadCsv = () => {
        const headers = [
            'No.', 'HISP',
            'Tol Err %', 'Untol Err %',
            'Tol Warn %', 'Untol Warn %',
            'Tol Alert %', 'Untol Alert %',
            'Total Input Count', 'Submission Time'
        ];
        const rows = sortedContributions.map((c, index) => {
            const untoleratedError   = c.errorCount   - c.toleratedErrorCount;
            const untoleratedWarning = c.warningCount - c.toleratedWarningCount;
            const untoleratedAlert   = c.alertCount   - c.toleratedAlertCount;
            return [
                index + 1,
                c.hisp,
                toPct(c.toleratedErrorCount,   c.inputRecordCount),
                toPct(untoleratedError,         c.inputRecordCount),
                toPct(c.toleratedWarningCount,  c.inputRecordCount),
                toPct(untoleratedWarning,       c.inputRecordCount),
                toPct(c.toleratedAlertCount,    c.inputRecordCount),
                toPct(untoleratedAlert,         c.inputRecordCount),
                c.inputRecordCount,
                convertDateToTimeString(c.submissionTs),
            ];
        });
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `lowest-scores${options > 0 ? `-top${options}` : ''}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }

    return (
        <TableContainer sx={{ mt: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '20px', marginBottom: '20px' }}>
                <FormControl sx={{ width: '200px' }}>
                    <InputLabel id='select-label'>Options</InputLabel>
                    <Select
                        labelId='select-label'
                        id='select-options'
                        value={options}
                        label='Options'
                        onChange={handleSelectChange}
                        size='small'
                    >
                        <MenuItem value={0}>Show All</MenuItem>
                        <MenuItem value={3}>Top 3</MenuItem>
                        <MenuItem value={5}>Top 5</MenuItem>
                        <MenuItem value={10}>Top 10</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    variant='outlined'
                    size='small'
                    startIcon={<DownloadIcon />}
                    onClick={downloadCsv}
                    disabled={!sortedContributions || sortedContributions.length === 0}
                >
                    Download CSV
                </Button>
            </div>
            <Table sx={{ m: 0 }}>
                <TableHead>
                    <StyledTableRowHeader>
                        <TableCell sx={{ width: 50 }}>#</TableCell>
                        <TableCell sx={{ width: 150 }}> </TableCell>
                        <TableCell>HISP</TableCell>
                        <TableCell>
                            <StyledInfoDiv>
                                Tol Err
                                <Tooltip title="toleratedErrorCount / inputRecordCount × 100" arrow placement='top'>
                                    <InfoIcon fontSize='small' sx={{ fontSize: 16 }} />
                                </Tooltip>
                            </StyledInfoDiv>
                        </TableCell>
                        <TableCell>
                            <StyledInfoDiv>
                                Untol Err
                                <Tooltip title="(errorCount − toleratedErrorCount) / inputRecordCount × 100" arrow placement='top'>
                                    <InfoIcon fontSize='small' sx={{ fontSize: 16 }} />
                                </Tooltip>
                            </StyledInfoDiv>
                        </TableCell>
                        <TableCell>
                            <StyledInfoDiv>
                                Tol Warn
                                <Tooltip title="toleratedWarningCount / inputRecordCount × 100" arrow placement='top'>
                                    <InfoIcon fontSize='small' sx={{ fontSize: 16 }} />
                                </Tooltip>
                            </StyledInfoDiv>
                        </TableCell>
                        <TableCell>
                            <StyledInfoDiv>
                                Untol Warn
                                <Tooltip title="(warningCount − toleratedWarningCount) / inputRecordCount × 100" arrow placement='top'>
                                    <InfoIcon fontSize='small' sx={{ fontSize: 16 }} />
                                </Tooltip>
                            </StyledInfoDiv>
                        </TableCell>
                        <TableCell>
                            <StyledInfoDiv>
                                Tol Alert
                                <Tooltip title="toleratedAlertCount / inputRecordCount × 100" arrow placement='top'>
                                    <InfoIcon fontSize='small' sx={{ fontSize: 16 }} />
                                </Tooltip>
                            </StyledInfoDiv>
                        </TableCell>
                        <TableCell>
                            <StyledInfoDiv>
                                Untol Alert
                                <Tooltip title="(alertCount − toleratedAlertCount) / inputRecordCount × 100" arrow placement='top'>
                                    <InfoIcon fontSize='small' sx={{ fontSize: 16 }} />
                                </Tooltip>
                            </StyledInfoDiv>
                        </TableCell>
                    </StyledTableRowHeader>
                </TableHead>
                <TableBody>
                    {sortedContributions && sortedContributions.map((c, index) => {
                        let logo = "";
                        if (hispsLogo.hasOwnProperty(c.hisp)) {
                            logo = hispsLogo[c.hisp];
                        }
                        const untoleratedError   = c.errorCount   - c.toleratedErrorCount;
                        const untoleratedWarning = c.warningCount - c.toleratedWarningCount;
                        const untoleratedAlert   = c.alertCount   - c.toleratedAlertCount;

                        return (
                            <StyledTableRow key={c.id}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                    {logo && <img src={logo} alt={c.hisp} style={{ maxWidth: 75, maxHeight: 50, marginTop: '8px' }} />}
                                </TableCell>
                                <TableCell>
                                    <Typography fontWeight='bold' fontSize='0.875rem'>{c.hisp}</Typography>
                                    <Typography fontSize='0.75rem' color='#999'>{convertDateToTimeString(c.submissionTs)}</Typography>
                                </TableCell>
                                <TableCell>{toPct(c.toleratedErrorCount,  c.inputRecordCount)}</TableCell>
                                <TableCell>{toPct(untoleratedError,        c.inputRecordCount)}</TableCell>
                                <TableCell>{toPct(c.toleratedWarningCount, c.inputRecordCount)}</TableCell>
                                <TableCell>{toPct(untoleratedWarning,      c.inputRecordCount)}</TableCell>
                                <TableCell>{toPct(c.toleratedAlertCount,   c.inputRecordCount)}</TableCell>
                                <TableCell>{toPct(untoleratedAlert,        c.inputRecordCount)}</TableCell>
                            </StyledTableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
